import type { Control, FieldValues, Path } from 'react-hook-form';

import { ListItem } from '@/features/list/components/ListItem';
import { isEmptyOrNull } from '@/shared/utils/string';
import type { SVGComponent } from '@/types';

import { GeoAreaSelectField } from './GeoAreaSelectField';

type Props<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  label: string;
  organizationId: string;
  depth?: number;
  sheetTitle?: string;
  helperText?: string;
  editable?: boolean;
  icon: SVGComponent;
  /**
   * Builds the badge shown when nothing is selected yet (e.g.
   * "Alle 42 Orte"). Receives the total number of sub-areas. When omitted
   * (or the count is 0), no badge is rendered for the empty state.
   */
  allPlacesLabel?: (count: number) => string;
};

/**
 * List-item variant of the geo-area picker: borderless row with leading icon,
 * used in composer-style forms (e.g. post creation). All logic lives in
 * {@link GeoAreaSelectField}; this component only supplies the `ListItem`
 * trigger. The selection (or the `allPlacesLabel` fallback) is shown as a
 * badge right of the title, next to the chevron.
 */
export const FormGeoAreaSelectListItem = <T extends FieldValues>({
  name,
  control,
  label,
  organizationId,
  depth,
  sheetTitle,
  helperText,
  editable = true,
  icon,
  allPlacesLabel
}: Props<T>) => (
  <GeoAreaSelectField
    name={name}
    control={control}
    label={label}
    organizationId={organizationId}
    depth={depth}
    sheetTitle={sheetTitle}
    helperText={helperText}
    editable={editable}
    renderTrigger={({ resolvedNames, totalDescendantCount, open }) => {
      const showAllPlaces =
        isEmptyOrNull(resolvedNames) &&
        allPlacesLabel !== undefined &&
        totalDescendantCount > 0;
      const badge = showAllPlaces
        ? allPlacesLabel(totalDescendantCount)
        : resolvedNames;

      return (
        <ListItem
          title={label}
          badge={isEmptyOrNull(badge) ? undefined : badge}
          icon={icon}
          hideRightArrow={!editable}
          onPress={editable ? open : undefined}
        />
      );
    }}
  />
);
